import styled from 'styled-components'
import { FormikProvider } from 'formik'
import TypographyPrimary from 'components/Typography/Primary'
import TypographySecondary from 'components/Typography/Secondary'
import CardWrapper from 'components/wrappers/CardWrapper'
import { ButtonPrimary, ButtonTertiary } from 'components/Button/Button'
import TextField from 'share-ui/components/TextField/TextField'
import { TextFieldTextType } from 'share-ui/components/TextField/TextFieldConstants'
import { StyledHorizontalDivider } from 'routes/ChatRouteLayout'
import { StyledPanelWrapper } from 'styles/panelStyles.css'
import usePayment from './usePayment'
import FormikTextField from 'components/TextFieldFormik'
import { StyledDeleteIcon } from 'styles/styledIcons.css'

import StringLoader from 'components/ContentLoaders/StringLoader'
import InputLoader from 'components/ContentLoaders/InputLoader'

const General = () => {
    const {
        payWithCardHandler,
        payWithTAOHandler,
        addCardHandler,
        balance,
        removePaymentCard,
        setAmount,
        amount,
        is_card_added,
        formik,
        payWithCardLoading,
        createCardLoading,
        removePaymentCardLoading,
        balanceLoading,
        updateAutoPaySettingsLoading,
        credits,
        taoLoading,
    } = usePayment()

    return (
        <StyledPanelWrapper>
            <CardWrapper>
                <StyledContentContainer>
                    <StyledHeader>
                        <TypographyPrimary size='x-large' value={`Balance:`} />

                        {balanceLoading ? (
                            <StringLoader />
                        ) : (
                            <TypographyPrimary
                                size='x-large'
                                value={`${balance ? `$${balance}` : '$0.00'}`}
                            />
                        )}

                        {!balanceLoading && !balance && (
                            <TypographyPrimary
                                size='x-large'
                                value={`Free Credits ${credits}`}
                                style={{ marginLeft: '40px' }}
                            />
                        )}
                    </StyledHeader>

                    <StyledBody>
                        <TypographySecondary
                            size='small'
                            value='Your current balance is the available credit for API calls. Click the button below to enter your card details and add the desired amount to your balance.'
                        />
                    </StyledBody>

                    <StyledHorizontalDivider />

                    <StyledBody>
                        <TypographyPrimary size='medium' semiBold value='Add Balance $' />

                        <StyledRow>
                            <TextField
                                value={amount}
                                type={TextFieldTextType.NUMBER}
                                placeholder='$0.000'
                                onChange={value => setAmount(value)}
                            />
                        </StyledRow>
                        {!balanceLoading && (
                            <StyledRow>
                                <ButtonPrimary
                                    disabled={payWithCardLoading}
                                    loading={payWithCardLoading}
                                    onClick={payWithCardHandler}
                                >
                                    Pay with Card
                                </ButtonPrimary>

                                <ButtonPrimary
                                    onClick={payWithTAOHandler}
                                    disabled={taoLoading}
                                    // loading={taoLoading}
                                >
                                    Pay with TAO
                                </ButtonPrimary>
                            </StyledRow>
                        )}
                    </StyledBody>
                </StyledContentContainer>
            </CardWrapper>

            <CardWrapper>
                <StyledContentContainer>
                    <StyledHeader>
                        <TypographyPrimary value='Automatic Payments' semiBold />
                    </StyledHeader>

                    <StyledBody>
                        <TypographySecondary
                            size='small'
                            value='Set up automatic billing by adding a card to your account. When your balance approaches the Auto-Pay threshold, we will automatically charge your saved card to reload Datura credits. This will occur at most once per hour, for the Auto-Pay amount specified below.'
                        />

                        <StyledRow>
                            <StyledImg src='https://cdn-icons-png.freepik.com/512/4341/4341764.png' />

                            {!balanceLoading && !is_card_added && (
                                <ButtonPrimary
                                    size='small'
                                    disabled={is_card_added || createCardLoading}
                                    loading={createCardLoading}
                                    onClick={addCardHandler}
                                >
                                    Add Card
                                </ButtonPrimary>
                            )}
                            {is_card_added && (
                                <ButtonTertiary
                                    onClick={removePaymentCard}
                                    loading={removePaymentCardLoading}
                                    disabled={removePaymentCardLoading}
                                >
                                    <StyledDeleteIcon />
                                    Remove
                                </ButtonTertiary>
                            )}
                        </StyledRow>
                        <FormikProvider value={formik}>
                            <StyledRow>
                                {balanceLoading ? (
                                    <>
                                        <InputLoader />
                                        <InputLoader />
                                    </>
                                ) : (
                                    <>
                                        <FormikTextField
                                            name='auto_pay_threshold'
                                            placeholder={'0.00'}
                                            label={'Auto-Pay Threshold'}
                                            disabled={!is_card_added}
                                        />
                                        <FormikTextField
                                            name='auto_pay_amount'
                                            placeholder={'0.00'}
                                            label={'Auto-Pay Amount'}
                                            disabled={!is_card_added}
                                        />

                                        <StyledSaveButton>
                                            <ButtonTertiary
                                                size='small'
                                                disabled={
                                                    !is_card_added || updateAutoPaySettingsLoading
                                                }
                                                loading={updateAutoPaySettingsLoading}
                                                onClick={formik.handleSubmit}
                                            >
                                                Save
                                            </ButtonTertiary>
                                        </StyledSaveButton>
                                    </>
                                )}
                            </StyledRow>
                        </FormikProvider>
                    </StyledBody>
                </StyledContentContainer>
            </CardWrapper>
        </StyledPanelWrapper>
    )
}
export default General

const StyledContentContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`

const StyledHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`

const StyledBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 5px;
    max-width: 800px;
`

const StyledRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    width: 400px;

    & + & {
        margin-top: 10px;
    }
`

const StyledImg = styled.img`
    object-fit: cover;
    margin-top: 10px;
    width: 150px;
    height: 100px;
`

const StyledSaveButton = styled.div`
    margin-top: 16px;

    min-width: 100px;
`
